import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { CultureService } from '@app/core/services/system-language/culture.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { ReportDataSource, ReportType } from '../../models/report-designer.model';
import { ReportDesignerService } from '../../services/report-designer.service';

@Component({
  selector: 'app-create-blank-report',
  templateUrl: './create-blank-report.component.html',
  styleUrls: ['./create-blank-report.component.scss']
})
export class CreateBlankReportComponent implements OnInit {
  form: UntypedFormGroup;
  reportTypes: ReportType[] = [];
  reportDataSources: ReportDataSource[] = [];
  cultures: Culture[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private reportDesignerService: ReportDesignerService,
    private cultureService: CultureService,
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: [null, Validators.required],
      description: [null],
      reportTypeId: [null, Validators.required],
      reportDataSourceId: [null, Validators.required],
      cultureId: [null, Validators.required],
    });

    this.loadDropdownOptions();
  }

  private loadDropdownOptions(): void {
    this.reportDesignerService.getReportTypes().subscribe({
      next: (res) => {
        const options = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        this.reportTypes = options;

        if (!this.form.value.reportTypeId && this.reportTypes.length > 0) {
          this.form.patchValue({ reportTypeId: this.reportTypes[0].id });
        }
      }
    });

    this.reportDesignerService.getAllReportDataSources().then((res) => {
      const options = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      this.reportDataSources = options;

      if (!this.form.value.reportDataSourceId && this.reportDataSources.length > 0) {
        this.form.patchValue({ reportDataSourceId: this.reportDataSources[0].id });
      }
    });

    this.cultureService.getCultures().subscribe({
      next: (cultures) => {
        this.cultures = cultures || [];

        if (!this.form.value.cultureId) {
          const defaultCulture = this.cultures.find(c => c.id === 'en-US') || this.cultures[0];
          if (defaultCulture) {
            this.form.patchValue({ cultureId: defaultCulture.id });
          }
        }
      }
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = {
      name: [{
        culture: this.form.value.cultureId,
        text: this.form.value.name
      }],
      description: this.form.value.description || null,
      reportTypeId: this.form.value.reportTypeId,
      reportDataSourceId: this.form.value.reportDataSourceId,
      cultureId: this.form.value.cultureId,
      filter: null,
      sort: null,
      columns: [
        'Employee.FirstName',
        'StartDate',
        'EndDate'
      ]
    };

    this.overlayService.show();

    this.reportDesignerService.createReport(payload).subscribe({
      next: (res) => {
        this.overlayService.hide();

        const reportId = res?.id || res?.reportId;

        if (reportId) {
          this.router.navigate(['/Reports/Designer', reportId]);
          return;
        }

        this.snackbarService.openSnackBar('Report created successfully', 'clear', 'success');
        this.router.navigate(['/Reports/Admin']);
      },
      error: () => {
        this.overlayService.hide();
        this.snackbarService.openSnackBar('Unable to create report', 'clear', 'warn');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/Reports/Admin']);
  }
}
